import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import JobFormModal from "@/components/JobFormModal";
import { useAuth } from "@/hooks/useAuth";
import { useRecruiterJobs, type NewRecruiterJob } from "@/hooks/useRecruiterJobs";
import {
  trackJobDepositOpen,
  trackJobDepositPublishedDirect,
  trackJobDepositDraftSaved,
} from "@/lib/analytics";

// Clé sessionStorage partagée avec Backoffice.tsx pour finaliser le dépôt
// d'annonce après création / connexion du compte recruteur.
const PENDING_JOB_DRAFT_KEY = "bnssa-mns:pending-job-draft";

/**
 * useJobDepositFlow — Flux mutualisé de dépôt d'annonce recruteur.
 *
 * Étape 1 : ouverture directe du formulaire de dépôt (JobFormModal), que le
 *           visiteur soit connecté ou non.
 * Étape 2 : à l'envoi du formulaire :
 *   • recruteur/admin connecté → création immédiate de l'annonce puis
 *     redirection vers le back-office ;
 *   • visiteur non connecté → l'annonce est stockée en brouillon
 *     (sessionStorage) et l'utilisateur est redirigé vers l'inscription
 *     recruteur ; le back-office finalise automatiquement le dépôt à la
 *     première connexion.
 *
 * Utilisé par le hero de /offres, le hero de la home et le header.
 */
export function useJobDepositFlow() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isRecruiter, isCandidate, isOrganisme, isAdmin } = useAuth();
  const { createJob } = useRecruiterJobs();
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => {
    // Garde de rôle : un candidat ou un organisme connecté ne peut pas déposer
    // une annonce. Les recruteurs connectés, les visiteurs non connectés et
    // l'admin restent autorisés.
    if (isAuthenticated && !isAdmin && (isCandidate || isOrganisme)) {
      toast.error(
        isOrganisme
          ? "Votre compte est un compte organisme de formation. Seuls les recruteurs peuvent publier une offre d'emploi."
          : "Votre compte est un compte candidat. Seuls les recruteurs peuvent publier une offre d'emploi."
      );
      return;
    }
    trackJobDepositOpen({ authenticated: isAuthenticated });
    setOpen(true);
  }, [isAuthenticated, isAdmin, isCandidate, isOrganisme]);
  const closeModal = useCallback(() => setOpen(false), []);

  const handleSaveJob = useCallback(
    async (data: NewRecruiterJob) => {
      // Cas 1 : recruteur/admin connecté → création immédiate
      if (isAuthenticated && user?.userId && (isRecruiter || isAdmin)) {
        try {
          await createJob(data, user.userId, user.email, (user as { userName?: string })?.userName);
          trackJobDepositPublishedDirect({
            category: data.category,
            contractType: data.contractType,
            region: data.region,
          });
          setOpen(false);
          toast.success("Annonce envoyée ! Elle sera publiée après validation.");
          navigate("/backoffice");
        } catch {
          toast.error("Impossible d'envoyer l'annonce. Réessayez.");
        }
        return;
      }
      // Cas 2 : non connecté (ou compte non recruteur) → brouillon + inscription
      try {
        sessionStorage.setItem(PENDING_JOB_DRAFT_KEY, JSON.stringify(data));
      } catch {
        // silent
      }
      trackJobDepositDraftSaved({
        category: data.category,
        contractType: data.contractType,
        region: data.region,
      });
      setOpen(false);
      toast("Créez votre compte recruteur pour finaliser le dépôt de votre annonce.", { icon: "📝" });
      navigate("/inscription?role=recruiter&next=/backoffice");
    },
    [isAuthenticated, user, isRecruiter, isAdmin, createJob, navigate]
  );

  const submitLabel =
    isAuthenticated && (isRecruiter || isAdmin) ? "Envoyer l'annonce" : "Continuer";

  // Repli : si un brouillon de pré-inscription existe (l'utilisateur a déjà
  // soumis la pop-up puis est revenu sans créer son compte), on le réinjecte
  // pour ne jamais afficher un formulaire vide.
  let fallbackDraft: NewRecruiterJob | null = null;
  if (open) {
    try {
      const raw = sessionStorage.getItem(PENDING_JOB_DRAFT_KEY);
      if (raw) fallbackDraft = JSON.parse(raw) as NewRecruiterJob;
    } catch {
      fallbackDraft = null;
    }
  }

  // Élément modal à rendre dans l'arbre du composant hôte.
  const modal = open ? (
    <JobFormModal
      existing={null}
      fallbackDraft={fallbackDraft}
      onClose={closeModal}
      onSave={handleSaveJob}
      submitLabel={submitLabel}
    />
  ) : null;

  return { open, openModal, closeModal, modal };
}
